import { tenantSlugFromPathOrHost } from '../utils/tenantDomains';
import { resolveApiBaseUrl } from './runtimeConfig';

const REPORT_ENDPOINT = '/api/client-errors';
const THROTTLE_MS = 60 * 1000;
const sentAtBySignature = new Map();

const redactSensitive = (value = '') => String(value || '')
  .replace(/(token|access_token|authorization|password|secret|api_key|key)=([^&\s]+)/gi, '$1=[redacted]')
  .replace(/Bearer\s+[A-Za-z0-9._-]+/g, 'Bearer [redacted]');

const safeTenantSlug = () => {
  try {
    return tenantSlugFromPathOrHost();
  } catch {
    return null;
  }
};

const safeWindowValue = (selector, fallback = '') => {
  try {
    return selector();
  } catch {
    return fallback;
  }
};

const makeSignature = (payload) => [
  payload.source || 'frontend',
  payload.tenantSlug || '',
  payload.path || '',
  payload.apiUrl || '',
  payload.status || '',
  payload.code || '',
  String(payload.message || '').slice(0, 180),
].join('|');

const shouldSend = (payload) => {
  const signature = makeSignature(payload);
  const now = Date.now();
  const last = sentAtBySignature.get(signature) || 0;
  if (now - last < THROTTLE_MS) return false;
  sentAtBySignature.set(signature, now);
  return true;
};

const normalizePayload = (payload = {}) => {
  const path = safeWindowValue(() => window.location.pathname, '');
  const url = safeWindowValue(() => window.location.href, '');
  const userAgent = safeWindowValue(() => window.navigator.userAgent, '');
  return {
    source: payload.source || 'frontend',
    message: redactSensitive(payload.message || 'Client-side problem reported'),
    stack: redactSensitive(payload.stack || ''),
    url: redactSensitive(payload.url || url),
    path: payload.path || path,
    tenantSlug: payload.tenantSlug || safeTenantSlug(),
    userAgent: payload.userAgent || userAgent,
    component: payload.component || '',
    status: payload.status,
    method: payload.method,
    apiUrl: redactSensitive(payload.apiUrl || ''),
    code: payload.code || '',
  };
};

export const reportClientError = (payload = {}) => {
  try {
    const normalized = normalizePayload(payload);
    if (!shouldSend(normalized)) return;

    const apiBase = resolveApiBaseUrl();
    const endpoint = `${apiBase}${REPORT_ENDPOINT}`;
    if (normalized.apiUrl && normalized.apiUrl.includes(REPORT_ENDPOINT)) return;

    const body = JSON.stringify(normalized);
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(endpoint, blob)) return;
    }

    if (typeof fetch !== 'undefined') {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Reporting must never break the player/admin app.
  }
};

const messageFromReason = (reason) => {
  if (!reason) return 'Unhandled promise rejection';
  if (reason.message) return reason.message;
  if (typeof reason === 'string') return reason;
  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
};

export const installClientErrorReporting = () => {
  if (typeof window === 'undefined' || window.__teamLobbyErrorReportingInstalled) return;
  window.__teamLobbyErrorReportingInstalled = true;

  window.addEventListener('error', (event) => {
    reportClientError({
      source: 'frontend',
      message: event.message || 'Uncaught browser error',
      stack: event.error?.stack || '',
      component: 'window.error',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportClientError({
      source: 'frontend',
      message: messageFromReason(reason),
      stack: reason?.stack || '',
      component: 'unhandledrejection',
    });
  });
};
